import { useState } from "react";
import { postLead } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal, ArchReveal } from "@/components/motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Store, MapPin } from "lucide-react";

const MODELS = [
  { id: "store", label: "Mağaza", desc: "Cadde veya AVM içi butik mağaza", icon: Store },
  { id: "kiosk", label: "Kiosk", desc: "AVM içi kompakt satış noktası", icon: MapPin },
];

const FORMATS = [
  {
    img: "/assets/img/store-90.jpeg", title: "90 m² Mağaza",
    sub: "Kompakt, verimli ve ilham veren alışveriş deneyimi",
    detail: "9 m × 10 m — AVM ve cadde için ideal başlangıç formatı",
  },
  {
    img: "/assets/img/store-150.jpeg", title: "150 m² Mağaza",
    sub: "Kompakt, verimli ve güçlü görünürlük",
    detail: "10 m × 15 m — güçlü AVM görünürlüğü, dengeli yatırım",
  },
  {
    img: "/assets/img/store-200.jpeg", title: "200 m² Mağaza",
    sub: "Daha fazla stil, daha fazla sen",
    detail: "10 m × 20 m — amiral gemisi deneyim, zengin ürün çeşitliliği",
  },
  {
    img: "/assets/img/kiosk-hero.jpeg", title: "3 × 3 m Kiosk",
    sub: "Zarafet detayda, güzellik seninle",
    detail: "Askılı dış cephe, cam tezgâh ve kompakt kasa kurgusu",
  },
];

const STEPS = ["İletişim", "Yatırım Modeli", "Lokasyon & Mesaj"];

export default function Franchise() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", investment_model: "store", city: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepValid = () => {
    if (step === 0) return form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && form.phone.trim().length >= 10;
    if (step === 1) return !!form.investment_model;
    return form.city.trim().length >= 2;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const r = await postLead(form);
      toast.success(r.message);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Başvuru gönderilemedi, tekrar dene");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full min-h-[52px] rounded-2xl border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold transition-colors";

  return (
    <>
      <Seo title="Bayilik" description="Kendi şehrinde bir Shule mağazası veya kiosku aç: 90/150/200 m² mağaza ve 3×3 m kiosk formatları." path="/bayilik" />
      <section className="bg-shule-cream/60 border-b border-shule-line">
        <div className="container-shule py-14 lg:py-20 max-w-3xl">
          <Reveal>
            <p className="eyebrow">Bayilik</p>
            <h1 className="mt-3 font-serif text-5xl lg:text-6xl font-light text-shule-coffee text-balance">
              Kendi şehrinde bir <span className="italic text-shule-goldhover">Shule</span> aç
            </h1>
            <p className="mt-5 text-shule-mocha leading-relaxed max-w-xl">
              Kemerli nişleri, sıcak aydınlatması ve topluluğuyla Shule deneyimini kendi şehrine taşı.
              Dört format arasından sana uyanı seç; anahtar teslim konsept, görsel ve kampanya desteği bizden.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-shule py-14 lg:py-20">
        <Reveal className="mb-10 text-center">
          <p className="eyebrow">Formatlar</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">Dört format, tek ruh</h2>
          <p className="mt-3 text-sm text-shule-soft">Açık meşe, kırık beyaz, pudra pembe ve rose gold — her formatta aynı sıcak atmosfer.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {FORMATS.map((f, i) => (
            <ArchReveal key={f.title} delay={i * 0.08}>
              <article data-testid={`format-card-${i}`} className="arch-card group h-full flex flex-col">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img src={f.img} alt={`Shule ${f.title} konsept panosu`} loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
                  <span className="absolute top-4 left-4 badge-tag !bg-shule-paper/90">{f.title}</span>
                </div>
                <div className="p-6 flex-1">
                  <h3 className="font-serif text-2xl leading-tight">{f.sub}</h3>
                  <p className="text-sm text-shule-soft mt-2">{f.detail}</p>
                </div>
              </article>
            </ArchReveal>
          ))}
        </div>
      </section>

      <div className="container-shule pb-14 lg:pb-20">
        <Reveal className="max-w-2xl mx-auto rounded-[2rem] border border-shule-line bg-shule-paper p-6 sm:p-10 shadow-[0_10px_30px_-10px_rgba(61,46,40,0.05)]">
          <p className="eyebrow mb-2">Başvuru</p>
          <h2 className="font-serif text-3xl mb-6">Üç adımda ön başvuru</h2>
          {done ? (
            <div data-testid="franchise-success" className="text-center py-10">
              <CheckCircle2 className="w-12 h-12 mx-auto text-shule-gold" />
              <h3 className="mt-4 font-serif text-3xl">Başvurun alındı</h3>
              <p className="mt-3 text-shule-mocha">Ekibimiz 3 iş günü içinde seninle iletişime geçecek. Şimdiden aramıza hoş geldin.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-8">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex-1">
                    <div data-testid={`franchise-progress-${i}`} className={`h-1.5 rounded-full transition-colors duration-500 ${i <= step ? "bg-shule-gold" : "bg-shule-line"}`} />
                    <p className={`mt-2 text-[11px] font-semibold ${i === step ? "text-shule-coffee" : "text-shule-soft"}`}>{s}</p>
                  </div>
                ))}
              </div>

              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fr-name" className="text-sm font-semibold block mb-2">Ad Soyad</label>
                    <input id="fr-name" data-testid="franchise-name-input" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Adın Soyadın" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fr-email" className="text-sm font-semibold block mb-2">E-posta</label>
                      <input id="fr-email" data-testid="franchise-email-input" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ornek@eposta.com" />
                    </div>
                    <div>
                      <label htmlFor="fr-phone" className="text-sm font-semibold block mb-2">Telefon</label>
                      <input id="fr-phone" data-testid="franchise-phone-input" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+90 5xx xxx xx xx" />
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <p className="text-sm font-semibold mb-4">Hangi modelle ilerlemek istersin?</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {MODELS.map((m) => (
                      <button key={m.id} data-testid={`franchise-model-${m.id}`} onClick={() => set("investment_model", m.id)}
                        className={`rounded-3xl border p-6 text-left transition-all duration-300 min-h-[120px] ${form.investment_model === m.id ? "border-shule-gold bg-shule-goldlight/30 shadow-[0_14px_30px_-12px_rgba(197,160,89,0.35)]" : "border-shule-lineaccent hover:border-shule-gold"}`}>
                        <m.icon className="w-6 h-6 text-shule-goldhover" />
                        <p className="mt-3 font-serif text-2xl">{m.label}</p>
                        <p className="text-xs text-shule-soft mt-1">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                  <div className="mt-5">
                    <label htmlFor="fr-budget" className="text-sm font-semibold block mb-2">Yatırım bütçe aralığın (opsiyonel)</label>
                    <input id="fr-budget" data-testid="franchise-budget-input" className={inputCls} value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Örn. 1 – 2 milyon TL" />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fr-city" className="text-sm font-semibold block mb-2">Hangi şehirde açmak istersin?</label>
                    <input id="fr-city" data-testid="franchise-city-input" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Örn. Eskişehir" />
                  </div>
                  <div>
                    <label htmlFor="fr-message" className="text-sm font-semibold block mb-2">Eklemek istediklerin (opsiyonel)</label>
                    <textarea id="fr-message" data-testid="franchise-message-input" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
                      className="w-full rounded-2xl border border-shule-lineaccent bg-shule-base px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none"
                      placeholder="Deneyimin, hedef lokasyonun, soruların…" />
                  </div>
                </div>
              )}

              <div className="mt-8 flex gap-3">
                {step > 0 && (
                  <button data-testid="franchise-back-button" onClick={() => setStep(step - 1)} className="btn-outline-shule !px-6">
                    <ArrowLeft className="w-4 h-4" /> Geri
                  </button>
                )}
                {step < 2 ? (
                  <button data-testid="franchise-next-button" onClick={() => stepValid() && setStep(step + 1)} disabled={!stepValid()}
                    className="btn-primary-shule flex-1 disabled:opacity-40">
                    Devam Et <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button data-testid="franchise-submit-button" onClick={submit} disabled={!stepValid() || loading}
                    className="btn-primary-shule flex-1 disabled:opacity-40">
                    {loading ? "Gönderiliyor…" : "Başvuruyu Gönder"}
                  </button>
                )}
              </div>
            </>
          )}
        </Reveal>
      </div>
    </>
  );
}
